import { SEV_STYLES, RISK_STYLES } from "../api";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

export default function ReportView({ report }) {
  if (!report) return null;
  const { property, documents, schedule, title_flow, findings, risk, suitability, conclusion } = report;
  const b = schedule?.boundaries || {};
  return (
    <div className="report-paper mx-auto max-w-4xl" data-testid="report-view">
      <p className="text-center text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">Confidential</p>
      <p className="mt-1 text-center font-mono text-[11px] font-bold uppercase tracking-widest text-blue-700">PropLoX AI Title Scrutiny</p>
      <h2 className="mt-4 text-center text-2xl font-bold tracking-tight text-slate-900" data-testid="report-title">Title Scrutiny Report</h2>
      <p className="mt-1 text-center text-xs text-slate-500">AI-Powered Property Document Verification — Karnataka</p>

      <div className="mt-8 border-t-2 border-slate-800 pt-6">
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900">Part I — Report Particulars</h3>
        <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          {[
            ["Report No", report.report_no], ["Date", report.date],
            ["Property Address", property?.address], ["Purpose", "Comprehensive AI Legal Title Scrutiny"],
            ["Survey No. / Plot No.", property?.survey_number || schedule?.survey_number || "—"],
            ["Village / Locality", property?.village || "—"],
            ["Taluk", property?.taluk || "—"], ["District", property?.district || "—"],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 border-b border-slate-100 py-1.5">
              <dt className="font-semibold text-slate-700">{k}</dt><dd className="text-right text-slate-600">{v || "—"}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="mt-8">
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900">Part II — Schedule of Property</h3>
        <dl className="mt-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-4 border-b border-slate-100 py-1.5"><dt className="font-semibold text-slate-700">Extent</dt><dd className="text-slate-600">{schedule?.extent || "—"}</dd></div>
          {["north", "south", "east", "west"].map((d) => (
            <div key={d} className="flex justify-between gap-4 border-b border-slate-100 py-1.5">
              <dt className="font-semibold capitalize text-slate-700">Boundary — {d}</dt><dd className="text-right text-slate-600">{b[d] || "—"}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="mt-8">
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900">Part III — Documents Scrutinized</h3>
        <table className="mt-4 w-full border-collapse text-xs">
          <thead>
            <tr className="bg-slate-100 text-left">
              {["S.No", "Doc. Type", "Date", "Doc. No.", "Executant", "Claimant", "Status"].map((h) => (
                <th key={h} className="border border-slate-200 px-2 py-1.5 font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(documents || []).map((d) => (
              <tr key={d.sno} data-testid={`report-doc-row-${d.sno}`}>
                <td className="border border-slate-200 px-2 py-1.5">{d.sno}</td>
                <td className="border border-slate-200 px-2 py-1.5">{d.doc_type}</td>
                <td className="border border-slate-200 px-2 py-1.5">{d.doc_date || "—"}</td>
                <td className="border border-slate-200 px-2 py-1.5">{d.doc_no || "—"}</td>
                <td className="border border-slate-200 px-2 py-1.5">{d.executant || "—"}</td>
                <td className="border border-slate-200 px-2 py-1.5">{d.claimant || "—"}</td>
                <td className="border border-slate-200 px-2 py-1.5">{d.verified ? "AI Verified" : "Uploaded"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8">
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900">Part IV — Flow of Title</h3>
        <div className="mt-3 space-y-2 text-sm leading-relaxed text-slate-700">
          {(title_flow || []).map((l, i) => <p key={i}>{l}</p>)}
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900">Cross-Document Verification Findings</h3>
        {(!findings || findings.length === 0) && (
          <p className="mt-3 flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" /> No discrepancies detected across documents.</p>
        )}
        <div className="mt-4 space-y-4">
          {(findings || []).map((f) => (
            <div key={f.id} className={`rounded-lg border p-4 ${SEV_STYLES[f.severity] || ""}`} data-testid={`report-finding-${f.id}`}>
              <p className="flex items-center gap-2 text-sm font-bold">
                <AlertTriangle className="h-4 w-4" /> [{f.severity.toUpperCase()}] {f.title} <span className="font-mono text-[10px] opacity-60">{f.id}</span>
              </p>
              <p className="mt-1.5 text-xs leading-relaxed">{f.description}</p>
              {f.evidence?.length > 0 && (
                <table className="mt-2 w-full border-collapse bg-white/70 text-[11px]">
                  <thead><tr className="text-left"><th className="border border-slate-200 px-2 py-1">Evidence</th><th className="border border-slate-200 px-2 py-1">Field</th><th className="border border-slate-200 px-2 py-1">Value</th></tr></thead>
                  <tbody>
                    {f.evidence.map((ev, i) => (
                      <tr key={i}><td className="border border-slate-200 px-2 py-1">{ev.document}</td><td className="border border-slate-200 px-2 py-1">{ev.field}</td><td className="border border-slate-200 px-2 py-1">{ev.value}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="mt-2 text-xs"><b>Recommendation:</b> {f.recommendation}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 border-t-2 border-slate-800 pt-6">
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900">Part V — Opinion Certificate</h3>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className={`rounded-full px-4 py-1.5 text-sm font-bold ${RISK_STYLES[risk?.level] || ""}`} data-testid="report-risk-badge">Risk: {risk?.level}</span>
          <span className="text-sm font-semibold text-slate-700" data-testid="report-suitability">{suitability}</span>
        </div>
        <p className="mt-4 text-sm font-bold text-slate-900">Conclusion</p>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-700">{conclusion}</p>
        <p className="mt-6 text-[11px] italic leading-relaxed text-slate-500">
          Generated by the PropLoX AI verification engine ({report.ai_engine}). Every finding is backed by the cited
          document evidence. This AI scrutiny does not replace a licensed advocate's opinion; original documents
          should be physically verified before high-value transactions.
        </p>
      </div>
    </div>
  );
}
